import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";

const features = [
  { icon: "✎", title: "Customized Obituary Builder", desc: "Beautiful, dignified prose generated in seconds — choose tone and length to match your family's voice." },
  { icon: "✦", title: "AI Portraits", desc: "Generate a tasteful memorial portrait from a description when no photograph is available." },
  { icon: "❝", title: "Memorial Quotes", desc: "Short, meaningful quotes for funeral programs and remembrance cards." },
  { icon: "❉", title: "Order of Service", desc: "A complete program drafted in moments, ready to print." },
  { icon: "✉", title: "Word of Acknowledgement", desc: "Gracious thank-you messages from the family, written for you." },
  { icon: "❍", title: "Photo Memorials", desc: "Add unlimited photographs to your obituary PDF — share by email, print, or download." },
];

export default function Landing() {
  const [config, setConfig] = useState({ annual_enabled: false, price_monthly_usd: 10, price_annual_usd: 99 });
  const [plan, setPlan] = useState("monthly");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const goSignup = () => nav(`/signup?plan=${plan}`);

  return (
    <div>
      {/* HERO — Sky / Clouds */}
      <section style={{
        position: "relative",
        minHeight: "92vh",
        overflow: "hidden",
        background: "linear-gradient(180deg, #0a1330 0%, #142654 30%, #1a2b56 55%, #14213f 78%, #0a0a0b 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "100px 24px 60px",
      }}>
        {/* Cloud / light photo overlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: 'url("https://images.unsplash.com/photo-1505533321630-975218a5f66f?auto=format&fit=crop&w=2400&q=70")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.42,
          mixBlendMode: "screen",
          pointerEvents: "none",
        }} />
        {/* Gold glow */}
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at 70% 25%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 45%), radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 60%)",
          pointerEvents: "none",
        }} />
        <div className="hero-grain" />

        <div style={{ position: "relative", maxWidth: 760, width: "100%", textAlign: "center" }}>
          <p className="serif" style={{ color: "rgba(230,230,239,0.85)", fontSize: "clamp(16px, 2vw, 19px)", lineHeight: 1.6, margin: "0 0 60px", fontWeight: 300, letterSpacing: "0.01em" }}>
            A peaceful writing space where letters become words, words become sentences, and sentences become paragraphs — creating a memorable obituary.
          </p>

          <h1 className="serif fade-in" style={{
            fontSize: "clamp(72px, 12vw, 140px)",
            lineHeight: 0.95,
            margin: "0 0 36px",
            color: "var(--gold)",
            fontWeight: 400,
            textShadow: "0 4px 24px rgba(0,0,0,0.5)",
            letterSpacing: "-0.01em",
          }}>
            <span style={{ display: "block" }}>Obituary</span>
            <span style={{ display: "inline-block", width: 64, height: 2, background: "rgba(212,175,55,0.7)", margin: "18px auto", verticalAlign: "middle" }} />
            <span style={{ display: "block" }}>Relief</span>
          </h1>

          <p className="serif" style={{ color: "#f5f5f7", fontSize: "clamp(17px, 2.2vw, 22px)", lineHeight: 1.55, margin: "40px auto 24px", maxWidth: 620, fontWeight: 300 }}>
            Remembering your beloved one — Love, Life &amp; Legacy. Grief can cause people to become at a loss for words, but with our support, we help you find the greatest words to say.
          </p>
          <p className="serif" style={{ color: "var(--gold)", fontSize: "clamp(15px, 1.9vw, 19px)", fontStyle: "italic", lineHeight: 1.6, margin: "0 auto 48px", maxWidth: 660, fontWeight: 400 }}>
            Even if you only have a few words to say, we will develop the full obituary for you with influential adjectives &amp; profound words.
          </p>

          <button data-testid="hero-begin" onClick={goSignup} className="btn-gold" style={{
            width: "100%",
            maxWidth: 520,
            padding: "20px 28px",
            fontSize: 19,
            borderRadius: 999,
            letterSpacing: "0.02em",
          }}>
            Begin
          </button>

          <div style={{ marginTop: 22, color: "rgba(230,230,239,0.85)", fontSize: 16 }}>
            Already remember someone here?{" "}
            <Link to="/login" data-testid="hero-signin" className="link-gold" style={{ fontWeight: 600 }}>Sign in</Link>
          </div>
        </div>

        {/* Mini-footer inside hero */}
        <div style={{ position: "absolute", bottom: 22, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 16, fontSize: 13 }}>
          <Link to="/privacy" data-testid="hero-footer-privacy" className="link-gold">Privacy</Link>
          <span className="muted">·</span>
          <Link to="/terms" data-testid="hero-footer-terms" className="link-gold">Terms</Link>
          <span className="muted">·</span>
          <Link to="/contact" data-testid="hero-footer-contact" className="link-gold">Contact</Link>
        </div>
      </section>

      {/* TAGLINE — kept */}
      <section style={{ position: "relative", padding: "80px 24px 60px", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.3em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 24 }}>
            ✦ The Obituary Builder ✦
          </div>
          <h2 className="serif" style={{ fontSize: "clamp(36px, 6vw, 64px)", lineHeight: 1.1, margin: "0 0 24px", color: "#fff", fontWeight: 400 }}>
            Words that <em style={{ color: "var(--gold)", fontStyle: "italic" }}>honor</em> a life,
            <br />composed with grace.
          </h2>
          <p className="muted" style={{ fontSize: 18, lineHeight: 1.7, maxWidth: 640, margin: "0 auto" }}>
            Obituary Relief helps families and funeral professionals write beautiful obituaries, orders of service,
            acknowledgements, and memorial keepsakes — guided by integrity, grounded in dignity.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ padding: "60px 24px", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div className="gold-line" />
            <h2 className="serif" style={{ fontSize: "clamp(32px, 5vw, 48px)", color: "#fff", margin: "24px 0 12px", fontWeight: 400 }}>
              Everything a family needs
            </h2>
            <p className="muted" style={{ maxWidth: 520, margin: "0 auto", fontSize: 16 }}>
              From the first draft to the printed program — your complete obituary toolkit.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {features.map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`} className="bg-card" style={{ padding: 28 }}>
                <div style={{ fontSize: 28, color: "var(--gold)", marginBottom: 14 }}>{f.icon}</div>
                <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>{f.title}</div>
                <div className="muted" style={{ fontSize: 14, lineHeight: 1.65 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section style={{ padding: "60px 24px", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div className="gold-line" />
            <h2 className="serif" style={{ fontSize: "clamp(32px, 5vw, 48px)", color: "#fff", margin: "24px 0 12px", fontWeight: 400 }}>
              How it works
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>
            {[
              { n: "01", t: "Tell us about your loved one", d: "Name, dates, a few cherished details. Pick a tone — warm, formal, celebratory, or brief." },
              { n: "02", t: "AI composes a draft", d: "Our writer (powered by Claude) drafts a beautiful obituary you can edit freely." },
              { n: "03", t: "Add photos & share", d: "Generate an AI portrait, upload unlimited photos, then share by email or download a PDF." },
            ].map((s, i) => (
              <div key={i} className="bg-card" style={{ padding: 28 }}>
                <div className="serif" style={{ fontSize: 40, color: "var(--gold)", lineHeight: 1, marginBottom: 12 }}>{s.n}</div>
                <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>{s.t}</div>
                <div className="muted" style={{ fontSize: 14, lineHeight: 1.65 }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYMPATHY GIFT SHOP — visible to all visitors */}
      <section style={{ padding: "60px 24px", borderTop: "1px solid var(--border)", background: "linear-gradient(180deg, #0a0a0b 0%, #0f0d08 100%)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div className="gold-line" />
            <h2 className="serif" style={{ fontSize: "clamp(32px, 5vw, 48px)", color: "#fff", margin: "24px 0 8px", fontWeight: 400 }}>
              Memorial Marketplace
            </h2>
            <p className="muted" style={{ fontSize: 15, lineHeight: 1.7, maxWidth: 660, margin: "0 auto" }}>
              Visitors to your loved one&apos;s memorial page can send heartfelt tributes — virtual candles, sympathy cards, flowers, and cinematic tribute videos. They can also make a <b style={{ color: "var(--gold)" }}>memorial donation directly to the family</b> to help with funeral costs, medical bills, or a charitable cause chosen by the decedent. Every gift and donation is delivered instantly by email or text, and 90% of every donation transfers straight to the family&apos;s connected bank account.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
            {[
              { icon: "🕯", title: "Virtual Candle", price: "Free", desc: "Light a candle and leave a short message on the memorial. Visible to all visitors." },
              { icon: "💝", title: "Memorial Donation", price: "Any amount", desc: "Friends and visitors can donate directly to the family to help with funeral costs, medical bills, or a charitable cause chosen by the decedent. 90% of every donation transfers straight to the family's bank account via Stripe (10% supports the platform)." },
              { icon: "💌", title: "Digital Sympathy Card", price: "$3.99", desc: "An elegantly designed condolence card — delivered by text or email, viewable on a private link." },
              { icon: "💐", title: "Digital Flower Bouquet", price: "$5.99", desc: "Animated, watermarked floral arrangement sent to the family by text or email. Screenshot-protected." },
              { icon: "▶", title: "Slideshow Tribute Video", price: "$9.99", desc: "A 3-minute photo slideshow with music — Ken Burns effect, your photos + a soundtrack of your choice. Preview free." },
              { icon: "📜", title: "Rhyming Memorial Poem", price: "$8.99", desc: "An AI-composed 16-line rhyming poem about your loved one. Editable. Preview free, pay only to unlock the printable PDF." },
              { icon: "💎", title: "Complete Memorial Pack", price: "$24.99 (save $4)", desc: "Bundle: 1 Slideshow Tribute + 1 Rhyming Poem + 1 Sympathy Gift. Credits auto-apply to your next previews." },
              { icon: "📜", title: "Printed Programs by Mail", price: "from $62.99 / 100", desc: "Professionally printed memorial programs shipped to the family within 5–7 business days." },
              { icon: "📨", title: "Prayer & Thank-You Cards", price: "Included", desc: "Print-ready 4-up prayer cards (4.25×5.5\") and 2-up thank-you cards (5×7\") with your obituary's theme." },
            ].map((p, i) => (
              <div key={i} className="bg-card" style={{ padding: 24, border: "1px solid #2a2a2a", display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{p.icon}</div>
                <div className="serif" style={{ fontSize: 20, color: "#fff", marginBottom: 6 }}>{p.title}</div>
                <div style={{ color: "var(--gold)", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", marginBottom: 12 }}>{p.price}</div>
                <div className="muted" style={{ fontSize: 13, lineHeight: 1.65, flex: 1 }}>{p.desc}</div>
              </div>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 12, textAlign: "center", marginTop: 24 }}>
            Available on every public memorial page. Subscribers can also send virtual gifts to fellow families.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section style={{ padding: "60px 24px", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div className="gold-line" />
            <h2 className="serif" style={{ fontSize: "clamp(32px, 5vw, 48px)", color: "#fff", margin: "24px 0 12px", fontWeight: 400 }}>
              Simple pricing
            </h2>
          </div>

          {config.annual_enabled && (
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 22, padding: 4, background: "var(--bg-3)", borderRadius: 999, border: "1px solid var(--border)", maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
              <button data-testid="plan-toggle-monthly" onClick={() => setPlan("monthly")} style={{
                flex: 1, padding: "10px 16px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 14, fontFamily: "inherit", fontWeight: 600,
                background: plan === "monthly" ? "var(--gold)" : "transparent",
                color: plan === "monthly" ? "#0a0a0b" : "var(--silver)",
              }}>Monthly</button>
              <button data-testid="plan-toggle-annual" onClick={() => setPlan("annual")} style={{
                flex: 1, padding: "10px 16px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 14, fontFamily: "inherit", fontWeight: 600,
                background: plan === "annual" ? "var(--gold)" : "transparent",
                color: plan === "annual" ? "#0a0a0b" : "var(--silver)",
                position: "relative",
              }}>
                Annual
                <span style={{ position: "absolute", top: -8, right: -4, background: "#1d6d3a", color: "#fff", fontSize: 10, padding: "2px 6px", borderRadius: 999, fontWeight: 700 }}>SAVE 17%</span>
              </button>
            </div>
          )}

          <div className="bg-card-gold" style={{ padding: 36, textAlign: "center" }}>
            <div className="label-dark">{plan === "annual" ? "Annual" : "Monthly"}</div>
            <div className="serif" style={{ fontSize: 64, color: "var(--gold)", lineHeight: 1, margin: "14px 0 6px" }}>
              ${plan === "annual" ? config.price_annual_usd : config.price_monthly_usd}
            </div>
            <div className="muted" style={{ fontSize: 14, marginBottom: 4 }}>
              {plan === "annual" ? "per year, auto-renewing" : "per month, auto-renewing"}
            </div>
            {plan === "annual" && (
              <div style={{ fontSize: 12, color: "#7ad19a", marginBottom: 18 }}>
                ≈ $8.25 / month — save $21 vs monthly
              </div>
            )}
            {plan !== "annual" && <div style={{ marginBottom: 24 }} />}
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", textAlign: "left" }}>
              {["Unlimited AI obituaries", "AI portraits & quotes", "Order of Service generator", "Word of Acknowledgement", "PDF downloads with photos", "Email & link sharing", "Free templates included"].map(b => (
                <li key={b} style={{ padding: "8px 0", color: "var(--silver)", fontSize: 15, display: "flex", gap: 10 }}>
                  <span style={{ color: "var(--gold)" }}>✓</span>{b}
                </li>
              ))}
            </ul>
            <button onClick={goSignup} data-testid="pricing-cta" className="btn-gold" style={{ padding: "14px 36px" }}>
              Subscribe &amp; Start Writing
            </button>
            <div className="muted" style={{ fontSize: 12, marginTop: 14 }}>Cancel anytime from your dashboard.</div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "60px 24px", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="gold-line" />
            <h2 className="serif" style={{ fontSize: "clamp(32px, 5vw, 48px)", color: "#fff", margin: "24px 0 12px", fontWeight: 400 }}>
              Questions
            </h2>
          </div>
          {[
            { q: "Is my information private?", a: "Yes. Your obituary drafts are visible only to you. We use industry-standard encryption and never sell your data." },
            { q: "Can I edit the AI's writing?", a: "Absolutely. Every draft is fully editable. The AI is here to begin — you finish." },
            { q: "What if I don't have a photo?", a: "Our AI portrait generator can create a tasteful memorial portrait from a written description." },
            { q: "How do I cancel?", a: "Cancel anytime from your dashboard. Your subscription stops at the end of the billing period — no questions asked." },
          ].map((f, i) => (
            <details key={i} className="bg-card" style={{ padding: "20px 24px", marginBottom: 12 }}>
              <summary className="serif" style={{ fontSize: 18, color: "#fff", cursor: "pointer", listStyle: "none" }}>{f.q}</summary>
              <div className="muted" style={{ fontSize: 14, lineHeight: 1.7, marginTop: 12 }}>{f.a}</div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
